import React, { useMemo, useState, useEffect } from 'react';
import { impactsData } from '../../data/impactsData';

const RecommendedMeasures = ({ indicatorName, sector }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isCustomExpanded, setIsCustomExpanded] = useState(false);
  const [customMeasures, setCustomMeasures] = useState([]);
  const [newMeasure, setNewMeasure] = useState('');

  // Load custom measures from localStorage
  useEffect(() => {
    if (indicatorName) {
      const stored = localStorage.getItem(`customMeasures_${indicatorName}`);
      if (stored) {
        try {
          setCustomMeasures(JSON.parse(stored));
        } catch (e) {
          console.error('Error loading custom measures:', e);
        }
      }
    }
  }, [indicatorName]);

  // Save custom measures to localStorage
  const saveCustomMeasures = (measures) => {
    if (indicatorName) {
      localStorage.setItem(`customMeasures_${indicatorName}`, JSON.stringify(measures));
    }
  };

  const measures = useMemo(() => {
    // Find the indicator in impactsData and return recommended measures for any indicator that has them
    for (const impact of impactsData) {
      for (const sec of impact.sectors) {
        if (sec.name === sector) {
          const indicator = sec.indicators.find(ind => ind.name === indicatorName);
          if (indicator && indicator.recommendedMeasures && indicator.recommendedMeasures.length > 0) {
            return indicator.recommendedMeasures;
          }
        }
      }
    }
    return null;
  }, [indicatorName, sector]);

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  const toggleCustomExpanded = () => {
    setIsCustomExpanded(!isCustomExpanded);
  };

  const handleAddMeasure = () => {
    if (newMeasure.trim()) {
      const updated = [...customMeasures, newMeasure.trim()];
      setCustomMeasures(updated);
      saveCustomMeasures(updated);
      setNewMeasure('');
    }
  };

  const handleRemoveMeasure = (index) => {
    const updated = customMeasures.filter((_, i) => i !== index);
    setCustomMeasures(updated);
    saveCustomMeasures(updated);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddMeasure();
    }
  };

  // Combine recommended and custom measures - custom measures appear in Recommended Measures section
  const allMeasures = useMemo(() => {
    const combined = [];
    if (measures) {
      measures.forEach((measure, index) => {
        combined.push({ text: measure, isCustom: false, index });
      });
    }
    customMeasures.forEach((measure, index) => {
      combined.push({ text: measure, isCustom: true, customIndex: index });
    });
    return combined;
  }, [measures, customMeasures]);

  // Show component if there are recommended measures OR custom measures OR we want to allow adding measures
  const showComponent = measures !== null || customMeasures.length > 0 || indicatorName;

  if (!showComponent) {
    return null;
  }

  return (
    <div className="recommended-measures-container">
      <h4 
        className="recommended-measures-header"
        onClick={toggleExpanded}
        style={{ cursor: 'pointer', userSelect: 'none' }}
      >
        <span className={`recommended-measures-arrow ${isExpanded ? 'expanded' : ''}`}>
          ▶
        </span>
        HEAT RISK REDUCTION MEASURES
      </h4>
      {isExpanded && (
        <>
          {allMeasures.length > 0 && (
            <ul className="recommended-measures-list">
              {allMeasures.map((item, index) => (
                <li 
                  key={item.isCustom ? `custom-${item.customIndex}` : `recommended-${item.index}`}
                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                  <span>{item.text}</span>
                  {item.isCustom && (
                    <button
                      onClick={() => handleRemoveMeasure(item.customIndex)}
                      className="remove-measure-btn"
                      style={{
                        background: '#dc3545',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        padding: '0.25rem 0.5rem',
                        cursor: 'pointer',
                        fontSize: '0.75rem',
                        marginLeft: '0.5rem',
                        minWidth: '24px',
                        height: '24px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                      title="Remove measure"
                    >
                      ×
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}

        </>
      )}

      {/* "Do you want to add any measures?" section - always shown below Recommended Measures */}
      <div style={{ marginTop: '1rem' }}>
        <h4 
          className="recommended-measures-header"
          onClick={toggleCustomExpanded}
          style={{ cursor: 'pointer', userSelect: 'none' }}
        >
          <span className={`recommended-measures-arrow ${isCustomExpanded ? 'expanded' : ''}`}>
            ▶
          </span>
          Do you want to add any measures? If yes, click here.
        </h4>
        {isCustomExpanded && (
          <div className="custom-measures-section">
            {/* Add new measure input */}
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
              <input
                type="text"
                value={newMeasure}
                onChange={(e) => setNewMeasure(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Enter your custom measure..."
                style={{
                  flex: 1,
                  padding: '0.5rem',
                  border: '1px solid #dee2e6',
                  borderRadius: '4px',
                  fontSize: '0.9rem'
                }}
              />
              <button
                onClick={handleAddMeasure}
                disabled={!newMeasure.trim()}
                style={{
                  padding: '0.5rem 1rem',
                  background: newMeasure.trim() ? '#28a745' : '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: newMeasure.trim() ? 'pointer' : 'not-allowed',
                  fontSize: '0.9rem',
                  fontWeight: 500
                }}
              >
                Add
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RecommendedMeasures;

